import FuturesEvidencePanel from './FuturesEvidencePanel'

type ReportKind = 'summary' | 'margin-report' | 'cash-balance' | 'position' | 'pnl' | 'check-futures' | 'reconciliation'

interface Props {
  kind: ReportKind
}

const reportMeta = {
  summary: {
    title: 'Summary',
    accessLabel: 'SUMMARY',
    target: 'frmFuturesMainSubFormReportSum',
    description: 'Default Futures landing screen and summary report container.',
    evidence: [
      'form_frmFuturesMainSubFormReportSum.txt',
      'form_frmFuturesRpSummary.txt',
      'form_frmFuturesQryRpSummary*.txt',
      'report_rpFuturesSummary.txt',
    ],
    notes: ['Access Form_Load sets navFutures.SourceObject to frmFuturesMainSubFormReportSum.'],
  },
  'margin-report': {
    title: 'Margin Report',
    accessLabel: 'Margin Report',
    target: 'frmFuturesMainSubFormReport -> rpFuturesMargin',
    description: 'Embedded margin report. Report output must follow Access report/query evidence.',
    evidence: [
      'form_frmFuturesMainSubFormReport.txt',
      'form_frmFuturesRpMargin.txt',
      'form_frmFuturesQryReportMarginChange.txt',
      'report_rpFuturesMargin.txt',
    ],
    notes: ['Parent click handler swaps embedded report source object to rpFuturesMargin.'],
  },
  'cash-balance': {
    title: 'Cash Balance',
    accessLabel: 'Cash Balance',
    target: 'form_frmFuturesSummary_CashBalance_*',
    description: 'Summary child report for bank cash balances.',
    evidence: [
      'form_frmFuturesSummary_CashBalance_Vietinbank.txt',
      'form_frmFuturesSummary_CashBalance_MBBank.txt',
      'form_frmFuturesSummary_CashBalance_BIDV.txt',
      'form_frmFuturesRpSummary.txt',
    ],
    notes: ['This is a web navigation child derived from summary evidence, not a direct parent nav button.'],
  },
  position: {
    title: 'Position',
    accessLabel: 'Position',
    target: 'form_frmFuturesSummary_Position*',
    description: 'Summary child report for Futures positions.',
    evidence: [
      'form_frmFuturesSummary_Position.txt',
      'form_frmFuturesSummary_Position_All.txt',
      'form_frmFuturesQryRpSummaryPos.txt',
      'form_frmFuturesQryRpSummaryPos2.txt',
    ],
    notes: ['Read both position summary forms before implementing calculations or totals.'],
  },
  pnl: {
    title: 'PnL',
    accessLabel: 'PnL',
    target: 'form_frmFuturesSummary_PnL',
    description: 'Summary child report for Futures profit and loss.',
    evidence: ['form_frmFuturesSummary_PnL.txt', 'form_frmFuturesRpSummary.txt', 'report_rpFuturesSummary.txt'],
    notes: ['Do not recreate PnL math from route label; use Access summary/report evidence.'],
  },
  'check-futures': {
    title: 'Check Futures',
    accessLabel: 'Check Futures',
    target: 'form_frmFuturesSummary_CheckFutures*',
    description: 'Summary child report for checking Futures orders/positions.',
    evidence: [
      'form_frmFuturesSummary_CheckFutures.txt',
      'form_frmFuturesSummary_CheckFutures_LD.txt',
      'form_frmFuturesSummary_CheckFutures_NY.txt',
    ],
    notes: ['LD and NY variants are separate evidence files.'],
  },
  reconciliation: {
    title: 'Reconciliation',
    accessLabel: 'Reconciliation',
    target: 'form_frmFuturesSummary_Reconcillation_*',
    description: 'Summary child report for bank reconciliation.',
    evidence: [
      'form_frmFuturesSummary_Reconcillation_Vietinbank.txt',
      'form_frmFuturesSummary_Reconcillation_MBBank.txt',
      'form_frmFuturesSummary_Reconcillation_BIDV.txt',
    ],
    notes: ['Access file name uses Reconcillation spelling; keep evidence names exact.'],
  },
} satisfies Record<ReportKind, { title: string; accessLabel: string; target: string; description: string; evidence: string[]; notes: string[] }>

export default function FuturesReportView({ kind }: Props) {
  const meta = reportMeta[kind]

  return (
    <FuturesEvidencePanel
      title={meta.title}
      accessLabel={meta.accessLabel}
      accessTarget={meta.target}
      description={meta.description}
      evidence={meta.evidence}
      notes={meta.notes}
    />
  )
}
